import { clsx, type ClassValue } from 'clsx'

/**
 * Utility function to combine and conditionally apply CSS classes
 * Uses clsx for better performance and smaller bundle size
 */
export function cn(...inputs: ClassValue[]) {
    return clsx(inputs)
}

/**
 * Debounce function to limit the rate of function execution
 */
export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null

    return (...args: Parameters<T>) => {
        if (timeout) clearTimeout(timeout)
        timeout = setTimeout(() => func(...args), wait)
    }
}

/**
 * Throttle function to limit function execution frequency
 */
export function throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
): (...args: Parameters<T>) => void {
    let inThrottle: boolean = false

    return (...args: Parameters<T>) => {
        if (!inThrottle) {
            func(...args)
            inThrottle = true
            setTimeout(() => (inThrottle = false), limit)
        }
    }
}

/**
 * Format phone number for WhatsApp URL
 */
export function formatWhatsAppNumber(phone: string): string {
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '')

    // Add country code if not present (assuming Mexico +52)
    if (!cleaned.startsWith('52') && cleaned.length === 10) {
        return `52${cleaned}`
    }

    return cleaned
}

/**
 * Generate WhatsApp URL with pre-filled message
 */
export function generateWhatsAppUrl(phone: string, message: string = ''): string {
    const formattedPhone = formatWhatsAppNumber(phone)
    const encodedMessage = encodeURIComponent(message)
    return `https://wa.me/${formattedPhone}${message ? `?text=${encodedMessage}` : ''}`
}

/**
 * Check if element is in viewport
 */
export function isInViewport(element: Element): boolean {
    const rect = element.getBoundingClientRect()
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    )
}

/**
 * Smooth scroll to element
 */
export function scrollToElement(elementId: string, offset: number = 0): void {
    const element = document.getElementById(elementId)
    if (element) {
        const elementPosition = element.offsetTop - offset
        window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
        })
    }
}

/**
 * Get scroll progress percentage
 */
export function getScrollProgress(): number {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
    return Math.min(Math.max((scrollTop / scrollHeight) * 100, 0), 100)
}

/**
 * Format number with commas
 */
export function formatNumber(num: number): string {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * Calculate reading time for text
 */
export function calculateReadingTime(text: string, wordsPerMinute: number = 200): number {
    const words = text.trim().split(/\s+/).length
    return Math.ceil(words / wordsPerMinute)
}

/**
 * Generate random ID
 */
export function generateId(length: number = 8): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
}

/**
 * Deep clone object
 */
export function deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') return obj
    if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T
    if (obj instanceof Array) return obj.map(item => deepClone(item)) as unknown as T
    if (typeof obj === 'object') {
        const clonedObj = {} as { [key: string]: any }
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                clonedObj[key] = deepClone(obj[key])
            }
        }
        return clonedObj as T
    }
    return obj
}

/**
 * Convert degrees to radians
 */
export function degToRad(degrees: number): number {
    return degrees * (Math.PI / 180)
}

/**
 * Convert radians to degrees
 */
export function radToDeg(radians: number): number {
    return radians * (180 / Math.PI)
}

/**
 * Linear interpolation
 */
export function lerp(start: number, end: number, factor: number): number {
    return start + (end - start) * factor
}

/**
 * Clamp number between min and max
 */
export function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max)
}

/**
 * Get random number between min and max
 */
export function random(min: number, max: number): number {
    return Math.random() * (max - min) + min
}

/**
 * Check if device is mobile
 */
export function isMobile(): boolean {
    return window.innerWidth <= 768
}

/**
 * Check if device is tablet
 */
export function isTablet(): boolean {
    return window.innerWidth > 768 && window.innerWidth <= 1024
}

/**
 * Check if device is desktop
 */
export function isDesktop(): boolean {
    return window.innerWidth > 1024
}

/**
 * Get device type
 */
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    if (isMobile()) return 'mobile'
    if (isTablet()) return 'tablet'
    return 'desktop'
}

/**
 * Preload image
 */
export function preloadImage(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve()
        img.onerror = reject
        img.src = src
    })
}

/**
 * Lazy load images
 */
export function lazyLoadImages(selector: string = 'img[data-src]'): void {
    const images = document.querySelectorAll(selector)

    if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const img = entry.target as HTMLImageElement
                    img.src = img.dataset.src || ''
                    img.classList.remove('lazy')
                    imageObserver.unobserve(img)
                }
            })
        })

        images.forEach(img => imageObserver.observe(img))
    } else {
        // Fallback for browsers without IntersectionObserver
        images.forEach(img => {
            const imgElement = img as HTMLImageElement
            imgElement.src = imgElement.dataset.src || ''
        })
    }
}

/**
 * Analytics helper
 */
export function trackEvent(
    action: string,
    category: string = 'general',
    label?: string,
    value?: number
): void {
    // Check if gtag is available in window object
    if (typeof window !== 'undefined' && 'gtag' in window && typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', action, {
            event_category: category,
            event_label: label,
            value: value
        })
    }
}

/**
 * Local storage helpers
 */
export const storage = {
    get: <T>(key: string, defaultValue: T): T => {
        try {
            const item = localStorage.getItem(key)
            return item ? JSON.parse(item) : defaultValue
        } catch {
            return defaultValue
        }
    },

    set: <T>(key: string, value: T): void => {
        try {
            localStorage.setItem(key, JSON.stringify(value))
        } catch (error) {
            console.warn('Failed to save to localStorage:', error)
        }
    },

    remove: (key: string): void => {
        try {
            localStorage.removeItem(key)
        } catch (error) {
            console.warn('Failed to remove from localStorage:', error)
        }
    },

    clear: (): void => {
        try {
            localStorage.clear()
        } catch (error) {
            console.warn('Failed to clear localStorage:', error)
        }
    }
}